import { STUDIO_SYNTH_LIBRARY } from './studioSynthLibrary.js';

/**
 * CookUp-hosted multisampled instruments.
 *
 * The libraries' samples live in CookUp storage and stream to the browser one
 * note at a time (see hostedSampleStore.js). A session's patch only names the
 * instrument ({ library, instrument }); the zones come from the library's
 * manifest, which the build pipeline (scripts/build-hosted-library.mjs)
 * writes from the library's own SFZ files.
 *
 * Pure: shared by the studio, the headless edit engine and the build pipeline.
 */

export const HOSTED_LIBRARIES = Object.freeze({
  'salamander-grand': {
    id: 'salamander-grand',
    name: 'Salamander Grand Piano',
    version: 2,
    // CC BY 3.0 requires credit, a link to the licence, and saying the samples were changed.
    credit: 'Salamander Grand Piano by Alexander Holm (retuned by Markus Fiedler), CC BY 3.0 (creativecommons.org/licenses/by/3.0). Retuned, trimmed, level-matched and re-encoded by CookUp.',
    license: { name: 'CC BY 3.0', url: 'https://creativecommons.org/licenses/by/3.0/' },
    source: {
      author: 'Alexander Holm',
      url: 'https://freepats.zenvoid.org/Piano/acoustic-grand-piano.html',
      release: 'SalamanderGrandPiano-SFZ+FLAC-V3+20200602'
    },
    bitrate: 192,
    instruments: [
      {
        id: 'grand', name: 'Salamander Grand', category: 'Keys', sfz: 'SalamanderGrandPianoRetuned-V3+20200602.sfz',
        tags: ['Piano', 'Acoustic', 'Multisampled'], release: 1, reverb: 0.16, maxSeconds: 22,
        // Every C8 recording measures +99.5 cents (FFT, all 16 layers); the retuned SFZ only takes
        // off 38 (the A7 value), leaving the top keys 60 cents sharp. Bring them in line with A7–C7.
        tuneOverrides: [{ sample: 'C8v\\d+\\.flac$', tune: -99 }],
        // Then every recording is retuned from its measured partials into beatless octaves.
        stretchTune: true
      }
    ]
  },
  'meta-steel-guitar': {
    id: 'meta-steel-guitar', name: 'Meta Steel Guitar', version: 1,
    credit: 'Meta Steel Guitar by Meta Creative Audio (CC0 1.0). First-position mapping, trimmed, level-matched and re-encoded by CookUp.',
    license: { name: 'CC0 1.0', url: 'https://creativecommons.org/publicdomain/zero/1.0/' },
    source: {
      author: 'Meta Creative Audio', url: 'https://github.com/facebookincubator/Meta-open-sound',
      release: 'meta-steel-guitar-v1.0.0', revision: '251c0be57d5d1b3242b91f53e0cde62ab1cb42d9'
    },
    bitrate: 160,
    instruments: [{ id: 'steel', name: 'Steel Acoustic Guitar', category: 'Guitar', sfz: 'cookup-steel.sfz',
      tags: ['Guitar', 'Acoustic', 'Steel String', 'Picked', 'Multisampled'], release: 0.18, reverb: 0.08 }]
  },
  'meta-nylon-guitar': {
    id: 'meta-nylon-guitar', name: 'Meta Nylon Guitar', version: 1,
    credit: 'Meta Nylon Guitar by Meta Creative Audio (CC0 1.0). First-position mapping with discrete finger/pick velocity layers, trimmed, level-matched and re-encoded by CookUp.',
    license: { name: 'CC0 1.0', url: 'https://creativecommons.org/publicdomain/zero/1.0/' },
    source: {
      author: 'Meta Creative Audio', url: 'https://github.com/facebookincubator/Meta-open-sound',
      release: 'meta-nylon-guitar-v1.0.0', revision: '251c0be57d5d1b3242b91f53e0cde62ab1cb42d9'
    },
    bitrate: 160,
    instruments: [{ id: 'nylon', name: 'Nylon Acoustic Guitar', category: 'Guitar', sfz: 'cookup-nylon.sfz',
      tags: ['Guitar', 'Acoustic', 'Nylon String', 'Classical', 'Fingerstyle', 'Picked', 'Multisampled'], release: 0.25, reverb: 0.08 }]
  },
  'karoryfer-shinyguitar': {
    id: 'karoryfer-shinyguitar', name: 'Karoryfer Shinyguitar', version: 1,
    credit: 'Shinyguitar by D. Smolken / Karoryfer Samples (CC0 1.0). Separate pickup and microphone mappings with quieter release noises, trimmed, level-matched and re-encoded by CookUp.',
    license: { name: 'CC0 1.0', url: 'https://creativecommons.org/publicdomain/zero/1.0/' },
    source: {
      author: 'D. Smolken / Karoryfer Samples', url: 'https://github.com/sfzinstruments/karoryfer.shinyguitar',
      release: 'v1.002', revision: '57243cca85277dbcc120ce17c6178032f93c80f3'
    },
    bitrate: 160,
    instruments: [
      { id: 'electric', name: 'Shiny Electric Guitar', category: 'Guitar', sfz: 'cookup-shiny-electric.sfz',
        tags: ['Guitar', 'Electric', 'Clean', 'Archtop', 'Pickup', 'Jazz', 'R&B', 'Round Robin', 'Multisampled'], release: 0.25, reverb: 0.06 },
      { id: 'microphone', name: 'Shiny Archtop Microphone', category: 'Guitar', sfz: 'cookup-shiny-microphone.sfz',
        tags: ['Guitar', 'Archtop', 'Microphone', 'Acoustic', 'Jazz', 'Round Robin', 'Multisampled'], release: 0.25, reverb: 0.06 }
    ]
  },
  'mda-rhodes': {
    id: 'mda-rhodes', name: 'MDA Rhodes Electric Piano', version: 1,
    credit: 'MDA ePiano by Paul Kellett (c) 2008, JUCE source by M.I. Hollemans (c) 2021–2025, MIT (https://opensource.org/license/mit). Waveforms extracted, loops expanded with decay, level-matched and re-encoded by CookUp. Rhodes: third-party trademark, no endorsement.',
    license: {
      name: 'MIT', url: 'https://opensource.org/license/mit',
      text: 'mda VST plug-ins\n\nCopyright (c) 2008 Paul Kellett\nCopyright (c) 2021-2025 M.I. Hollemans (JUCE version)\n\nPermission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:\n\nThe above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.\n\nTHE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.'
    },
    source: {
      author: 'Paul Kellett; JUCE source by M.I. Hollemans', url: 'https://github.com/hollance/mda-plugins-juce/tree/552702479a9eac534f3abbf6e878a5eab473e0cb/EPiano',
      release: 'MDA ePiano recorded waveform table', revision: '552702479a9eac534f3abbf6e878a5eab473e0cb'
    },
    bitrate: 160,
    instruments: [{ id: 'rhodes', name: 'MDA Rhodes Electric Piano', category: 'Electric Piano', sfz: 'cookup-mda-rhodes.sfz',
      tags: ['Rhodes', 'Electric Piano', 'Tine', 'Vintage', 'R&B', 'Soul', 'Multisampled'], release: 0.3, reverb: 0.06, maxSeconds: 18 }]
  },
  'sullivan-wurlitzer': {
    id: 'sullivan-wurlitzer', name: 'Greg Sullivan Wurlitzer EP200', version: 1,
    credit: 'Wurlitzer EP200 by Greg Sullivan; SFZ by kinwie. CC BY 3.0: https://creativecommons.org/licenses/by/3.0/. Source: github.com/sfzinstruments/GregSullivan.E-Pianos. Loops expanded, level-matched and re-encoded by CookUp. Wurlitzer: third-party trademark, no endorsement.',
    license: { name: 'CC BY 3.0', url: 'https://creativecommons.org/licenses/by/3.0/' },
    source: {
      author: 'Greg Sullivan; SFZ mapping by kinwie', url: 'https://github.com/sfzinstruments/GregSullivan.E-Pianos',
      release: 'Wurlitzer EP200 v1.1, kinwie SFZ mapping', revision: '8c3e581acda3594b553948ff0222d4f84a698376'
    },
    bitrate: 160,
    instruments: [{ id: 'ep200', name: 'Wurlitzer EP200', category: 'Electric Piano', sfz: 'cookup-wurlitzer.sfz',
      tags: ['Wurlitzer', 'Electric Piano', 'Reed', 'Vintage', 'R&B', 'Soul', 'Multisampled'], release: 0.2, reverb: 0.06, maxSeconds: 30 }]
  },
  'karoryfer-black-blue-basses': {
    id: 'karoryfer-black-blue-basses', name: 'Karoryfer Black and Blue Basses', version: 1,
    credit: 'Black and Blue Basses by D. Smolken / Karoryfer Samples (CC0 1.0). Direct fingered, picked, ghost and staccato recordings; mapped to concert pitch, trimmed, level-matched and re-encoded by CookUp.',
    license: { name: 'CC0 1.0', url: 'https://creativecommons.org/publicdomain/zero/1.0/' },
    source: {
      author: 'D. Smolken / Karoryfer Samples', url: 'https://github.com/sfzinstruments/karoryfer.black-and-blue-basses',
      release: 'v1.002', revision: '6e7d674cdb41be7a54dbccb15472401ad01099b9'
    },
    bitrate: 160,
    instruments: [
      { id: 'fingered', name: 'Black Fingered Electric Bass', category: 'Bass', sfz: 'cookup-bass-fingered.sfz',
        tags: ['Bass', 'Electric', 'Fingered', 'Hollowbody', 'Five String', 'Round Robin', 'Multisampled', 'Black and Blue', 'Karoryfer'], release: 0.2, reverb: 0 },
      { id: 'picked', name: 'Blue Picked Electric Bass', category: 'Bass', sfz: 'cookup-bass-picked.sfz',
        tags: ['Bass', 'Electric', 'Picked', 'Solidbody', 'Five String', 'Round Robin', 'Multisampled', 'Black and Blue', 'Karoryfer'], release: 0.2, reverb: 0 },
      { id: 'ghost', name: 'Black Ghost Bass', category: 'Bass', sfz: 'cookup-bass-ghost.sfz',
        tags: ['Bass', 'Electric', 'Ghost', 'Muted', 'Percussive', 'Round Robin', 'Multisampled', 'Black and Blue', 'Karoryfer'], release: 0.2, reverb: 0 },
      { id: 'staccato', name: 'Black Staccato Bass', category: 'Bass', sfz: 'cookup-bass-staccato.sfz',
        tags: ['Bass', 'Electric', 'Staccato', 'Short', 'Round Robin', 'Multisampled', 'Black and Blue', 'Karoryfer'], release: 0.2, reverb: 0 }
    ]
  },
  'vsco2-ce': {
    id: 'vsco2-ce',
    name: 'VS Chamber Orchestra 2: Community Edition',
    version: 2,
    // CC0: no attribution required; credited anyway as a courtesy.
    credit: 'VS Chamber Orchestra 2: Community Edition by Versilian Studios (CC0). Tuned, level-matched and re-encoded by CookUp.',
    license: { name: 'CC0 1.0', url: 'https://creativecommons.org/publicdomain/zero/1.0/' },
    source: {
      author: 'Versilian Studios (Sam Gossner, Simon Dalzell)',
      url: 'https://github.com/sgossner/VSCO-2-CE',
      release: 'SFZ branch, build 1.1.1'
    },
    bitrate: 160,
    instruments: [
      { id: 'violins', autoTune: true, name: 'Orchestra Violins', category: 'Strings', sfz: 'ViolinEnsSusVib.sfz', tags: ['Strings', 'Ensemble', 'Sustain'], release: 0.6, reverb: 0.24 },
      { id: 'violas', autoTune: true, name: 'Orchestra Violas', category: 'Strings', sfz: 'ViolaEnsSusVib.sfz', tags: ['Strings', 'Ensemble', 'Sustain'], release: 0.6, reverb: 0.24 },
      { id: 'cellos', autoTune: true, name: 'Orchestra Cellos', category: 'Strings', sfz: 'CelloEnsSusVib.sfz', tags: ['Strings', 'Ensemble', 'Sustain'], release: 0.6, reverb: 0.24 },
      { id: 'basses', autoTune: true, name: 'Orchestra Basses', category: 'Strings', sfz: 'ContrabassSusVB.sfz', tags: ['Strings', 'Sustain'], release: 0.6, reverb: 0.22 },
      { id: 'violins-pizz', name: 'Violins Pizzicato', category: 'Strings', sfz: 'ViolinEnsPizz.sfz', tags: ['Strings', 'Pizzicato'], release: 0.4, reverb: 0.2 },
      { id: 'cellos-pizz', name: 'Cellos Pizzicato', category: 'Strings', sfz: 'CelloEnsPizz.sfz', tags: ['Strings', 'Pizzicato'], release: 0.4, reverb: 0.2 },
      { id: 'violins-spiccato', name: 'Violins Spiccato', category: 'Strings', sfz: 'ViolinEnsSpic.sfz', tags: ['Strings', 'Short'], release: 0.25, reverb: 0.2 },
      { id: 'flute', autoTune: true, name: 'Flute', category: 'Woodwinds', sfz: 'FluteSusVib.sfz', tags: ['Woodwind', 'Solo'], release: 0.4, reverb: 0.22 },
      { id: 'oboe', autoTune: true, name: 'Oboe', category: 'Woodwinds', sfz: 'OboeSusVib.sfz', tags: ['Woodwind', 'Solo'], release: 0.4, reverb: 0.22 },
      { id: 'clarinet', autoTune: true, name: 'Clarinet', category: 'Woodwinds', sfz: 'ClarinetSus.sfz', tags: ['Woodwind', 'Solo'], release: 0.4, reverb: 0.22 },
      { id: 'bassoon', autoTune: true, name: 'Bassoon', category: 'Woodwinds', sfz: 'BassoonSus.sfz', tags: ['Woodwind', 'Solo'], release: 0.4, reverb: 0.22 },
      { id: 'french-horn', autoTune: true, name: 'French Horn', category: 'Brass', sfz: 'FHornSus.sfz', tags: ['Brass', 'Solo'], release: 0.5, reverb: 0.24 },
      { id: 'trumpet', autoTune: true, name: 'Trumpet', category: 'Brass', sfz: 'TrumpetSus.sfz', tags: ['Brass', 'Solo'], release: 0.4, reverb: 0.22 },
      { id: 'trombone', autoTune: true, name: 'Trombone', category: 'Brass', sfz: 'TromboneSus.sfz', tags: ['Brass', 'Solo'], release: 0.4, reverb: 0.22 },
      { id: 'tuba', autoTune: true, name: 'Tuba', category: 'Brass', sfz: 'TubaSus.sfz', tags: ['Brass', 'Solo'], release: 0.4, reverb: 0.2 },
      { id: 'harp', autoTune: 'attack', name: 'Concert Harp', category: 'Plucked', sfz: 'Harp.sfz', tags: ['Harp', 'Plucked'], release: 3, reverb: 0.22 },
      { id: 'timpani', name: 'Timpani', category: 'Percussion', sfz: 'Timpani.sfz', tags: ['Percussion', 'Orchestral'], release: 4, reverb: 0.24 },
      { id: 'glockenspiel', name: 'Glockenspiel', category: 'Mallets', sfz: 'Glockenspiel.sfz', tags: ['Mallet', 'Bell'], release: 3, reverb: 0.2 },
      { id: 'marimba', name: 'Marimba', category: 'Mallets', sfz: 'Marimba.sfz', tags: ['Mallet'], release: 2, reverb: 0.18 },
      { id: 'tubular-bells', name: 'Tubular Bells', category: 'Mallets', sfz: 'TubularBells.sfz', tags: ['Bell', 'Orchestral'], release: 5, reverb: 0.22 }
    ]
  },
  [STUDIO_SYNTH_LIBRARY.id]: STUDIO_SYNTH_LIBRARY
});

export const hostedInstrumentKey = (library, instrument) => `${library}/${instrument}`;

export function hostedInstrumentDefinition(library, instrument) {
  return HOSTED_LIBRARIES[library]?.instruments.find((entry) => entry.id === instrument) || null;
}

/** Every hosted instrument as { library, instrument, definition }. */
export const HOSTED_INSTRUMENTS = Object.values(HOSTED_LIBRARIES).flatMap((library) => (
  library.instruments.map((definition) => ({ library: library.id, instrument: definition.id, definition }))
));

/**
 * The engine's zones for one instrument of a loaded manifest. Buffer ids are
 * the samples' content-addressed ids, so two instruments sharing a recording
 * share one download and one decoded buffer.
 */
export function hostedInstrumentZones(manifest, instrumentId) {
  const instrument = manifest?.instruments?.find((entry) => entry.id === instrumentId);
  if (!instrument) return [];
  return instrument.zones.map((zone, index) => ({
    id: `${instrumentId}-${index}`,
    bufferId: `hosted:${manifest.id}:${zone.sample}`,
    sampleId: zone.sample,
    name: zone.sample,
    sourceType: 'hosted',
    rootNote: zone.rootNote,
    loNote: zone.loNote,
    hiNote: zone.hiNote,
    loVelocity: zone.loVelocity,
    hiVelocity: zone.hiVelocity,
    gain: zone.gain ?? 1,
    pan: zone.pan ?? 0,
    tune: zone.tune ?? 0,
    pitchKeytrack: zone.pitchKeytrack ?? 1,
    velocityTrack: zone.velocityTrack ?? 1,
    start: zone.start ?? 0,
    end: zone.end ?? null,
    loopEnabled: zone.loopEnabled === true,
    loopStart: zone.loopStart ?? 0,
    loopEnd: zone.loopEnd ?? 0,
    // Studio Synth loops are crossfaded offline, with the boundary already aligned.
    loopCrossfade: HOSTED_LIBRARIES[manifest.id]?.renderedSynth ? 0 : 0.02,
    trigger: ['release', 'pedal-down', 'pedal-up'].includes(zone.trigger) ? zone.trigger : 'attack',
    roundRobin: zone.roundRobin ?? 0,
    release: zone.release ?? null,
    releaseDecay: zone.releaseDecay ?? 0
  }));
}

/**
 * The zones to have loaded before the first note: per key range, the one
 * recording nearest a typical playing velocity. Every key then sounds at once,
 * and the exact velocity layer replaces it as soon as it has streamed in.
 */
export function hostedStarterZones(zones, velocity = 0.7) {
  const byRange = new Map();
  zones.filter((zone) => zone.trigger === 'attack' && zone.roundRobin === 0).forEach((zone) => {
    const key = `${zone.loNote}:${zone.hiNote}`;
    const distance = velocity < zone.loVelocity ? zone.loVelocity - velocity
      : velocity > zone.hiVelocity ? velocity - zone.hiVelocity : 0;
    const best = byRange.get(key);
    if (!best || distance < best.distance) byRange.set(key, { zone, distance });
  });
  // The pedal's own sound too: it has no key to stream it on.
  const pedal = zones.filter((zone) => zone.trigger.startsWith('pedal') && zone.roundRobin === 0);
  return [...byRange.values()].map((entry) => entry.zone).concat(pedal);
}

/**
 * The physical model a hosted instrument falls back to if its library cannot
 * be loaded (for example a session saved while it was available): the part
 * keeps playing, in CookUp's modelled version of the same family, rather than
 * going silent.
 */
export function hostedFallbackModel(library, instrument) {
  const definition = hostedInstrumentDefinition(library, instrument);
  const id = definition?.id || '';
  switch (definition?.category) {
    case 'Keys': return 'piano';
    case 'Electric Piano': return 'piano';
    case 'Strings': return /pizz|spiccato/.test(id) ? 'pluck' : 'bowed';
    case 'Woodwinds': return id === 'flute' ? 'wind' : 'reed';
    case 'Brass': return 'brass';
    case 'Plucked': return 'pluck';
    case 'Guitar': return 'pluck';
    case 'Bass': return 'pluck';
    case 'Mallets': return /bells|glockenspiel/.test(id) ? 'bell' : 'mallet';
    case 'Percussion': return 'mallet';
    default: return 'piano';
  }
}
